import moment from 'moment'
import { useSelector } from 'react-redux'

import { type PortionSizes } from 'config/portionSizes'
import type { BasketAddress } from 'reducers/basket'
import {
  getBasketDate,
  getChosenAddress,
  getNumPortions,
  getSelectedDeliverySlot,
  isBasketTransactionalOrder,
} from 'selectors/basket'
import { getUserSubscriptionState } from 'selectors/user'
import { parseTimeRange } from 'utils/deliverySlot'
import type { DeliverySlot } from 'utils/types/order'

import { useGoustoAnytimeMenuVariant } from './useGoustoAnytimeMenuVariant'

export const GOUSTO_ANYTIME_FLAG = 'tomatoes_ofx_webclient_gousto_anytime'

// Shared fallback so tracking and the box-size editor agree on a missing numPortions.
export const DEFAULT_PORTION_SIZE: PortionSizes = 2

export const formatBoxSize = (numPortions: number | undefined): string =>
  numPortions ? `${numPortions} people` : ''

export const formatDeliveryDayShort = (basketDate: string | undefined): string => {
  if (!basketDate) return ''
  const m = moment(basketDate)

  return m.isValid() ? m.format('ddd D MMM') : ''
}

export const formatDeliverySlot = (slot: DeliverySlot | null): string => {
  if (!slot) return ''
  const start = slot.get('deliveryStartTime')
  const end = slot.get('deliveryEndTime')
  if (!start || !end) return ''

  return parseTimeRange(start, end)
}

export const formatDeliveryDayLong = (
  basketDate: string | undefined,
  slot: DeliverySlot | null,
): string => {
  if (!basketDate) return ''
  const m = moment(basketDate)
  if (!m.isValid()) return ''

  const day = m.format('dddd Do MMM')
  const time = formatDeliverySlot(slot)

  return time ? `${day}, ${time}` : day
}

export const formatPostcode = (address: BasketAddress | undefined): string =>
  address?.get('postcode') ?? ''

export const formatAddressName = (address: BasketAddress | undefined): string =>
  address?.get('name') ?? ''

export const formatAddressLines = (address: BasketAddress | undefined): string[] => {
  if (!address) return []
  const line1 = address.get('line1')
  const line2 = address.get('line2')
  const town = address.get('town')
  const postcode = address.get('postcode')

  return [line1, line2, [town, postcode].filter(Boolean).join(' ')].filter(
    (line): line is string => !!line,
  )
}

export const formatFullAddress = (address: BasketAddress | undefined): string =>
  formatAddressLines(address).join(', ')

export type DeliverySettings = {
  isEnabled: boolean
  boxSize: string
  deliveryDayShort: string
  deliveryDayLong: string
  postcode: string
  fullAddress: string
}

export const useDeliverySettings = (): DeliverySettings => {
  const isInExperimentVariant = useGoustoAnytimeMenuVariant()
  const numPortions = useSelector(getNumPortions)
  const basketDate = useSelector(getBasketDate)
  const chosenAddress = useSelector(getChosenAddress)
  const deliverySlot = useSelector(getSelectedDeliverySlot)
  const isTransactionalOrder = useSelector(isBasketTransactionalOrder)
  const userSubscriptionState = useSelector(getUserSubscriptionState)
  const isInactiveCustomer = userSubscriptionState === 'inactive'

  return {
    isEnabled: isInExperimentVariant && isTransactionalOrder && isInactiveCustomer,
    boxSize: formatBoxSize(numPortions),
    deliveryDayShort: formatDeliveryDayShort(basketDate),
    deliveryDayLong: formatDeliveryDayLong(basketDate, deliverySlot),
    postcode: formatPostcode(chosenAddress),
    fullAddress: formatFullAddress(chosenAddress),
  }
}
